import { useCallback, useMemo, useState } from 'react';

import { GridValidRowModel } from '@mui/x-data-grid-premium';

import { Dayjs } from 'dayjs';

import { GeneratikTableColumn } from '../GeneratikTable.types';
import { TimelineBooking, TimelineGranularity, TimelineVariant } from '../timeline/timeline.types';
import { toDayjs } from '../timeline/timeline.utils';

export interface UseTimelineVariantParams<R extends GridValidRowModel> {
  columns: GeneratikTableColumn<R>[];
  getRowBookings?: (row: R) => TimelineBooking[];
  variant?: TimelineVariant;
  onVariantChange?: (variant: TimelineVariant) => void;
  timelineGranularity?: TimelineGranularity;
  onTimelineGranularityChange?: (granularity: TimelineGranularity) => void;
  timelineMinDate?: Date | string | Dayjs;
  timelineMaxDate?: Date | string | Dayjs;
  timelineHighlightRange?: {
    start?: Date | string | Dayjs;
    end?: Date | string | Dayjs;
  };
}

export interface UseTimelineVariantReturn {
  variant: TimelineVariant;
  timelineGranularity: TimelineGranularity;
  handleVariantChange: (next: TimelineVariant) => void;
  handleTimelineGranularityChange: (next: TimelineGranularity) => void;
  hasTimelineColumns: boolean;
  isTimelineActive: boolean;
  normalizedTimelineMin: Dayjs | undefined;
  normalizedTimelineMax: Dayjs | undefined;
  normalizedHighlightRange: { start?: Dayjs; end?: Dayjs } | undefined;
}

const useTimelineVariant = <R extends GridValidRowModel>({
  columns,
  getRowBookings,
  variant: variantProp,
  onVariantChange,
  timelineGranularity: granularityProp,
  onTimelineGranularityChange,
  timelineMinDate,
  timelineMaxDate,
  timelineHighlightRange,
}: UseTimelineVariantParams<R>): UseTimelineVariantReturn => {
  const [internalVariant, setInternalVariant] = useState<TimelineVariant>('data');
  const [internalGranularity, setInternalGranularity] = useState<TimelineGranularity>('week');
  const variant: TimelineVariant = variantProp ?? internalVariant;
  const timelineGranularity: TimelineGranularity = granularityProp ?? internalGranularity;

  const handleVariantChange = useCallback(
    (next: TimelineVariant) => {
      if (onVariantChange) onVariantChange(next);
      else setInternalVariant(next);
    },
    [onVariantChange],
  );

  const handleTimelineGranularityChange = useCallback(
    (next: TimelineGranularity) => {
      if (onTimelineGranularityChange) onTimelineGranularityChange(next);
      else setInternalGranularity(next);
    },
    [onTimelineGranularityChange],
  );

  // `timeline` is `true` or a pin position, so anything but undefined/false counts.
  const hasTimelineColumns = useMemo(
    () => columns.some((col) => col.timeline !== undefined && col.timeline !== false),
    [columns],
  );
  const isTimelineActive = variant === 'timeline' && hasTimelineColumns && !!getRowBookings;

  const normalizedTimelineMin = useMemo(() => toDayjs(timelineMinDate), [timelineMinDate]);
  const normalizedTimelineMax = useMemo(() => toDayjs(timelineMaxDate), [timelineMaxDate]);

  const normalizedHighlightRange = useMemo(() => {
    const start = toDayjs(timelineHighlightRange?.start);
    const end = toDayjs(timelineHighlightRange?.end);
    if (!start && !end) return undefined;
    return { start, end };
  }, [timelineHighlightRange?.start, timelineHighlightRange?.end]);

  return {
    variant,
    timelineGranularity,
    handleVariantChange,
    handleTimelineGranularityChange,
    hasTimelineColumns,
    isTimelineActive,
    normalizedTimelineMin,
    normalizedTimelineMax,
    normalizedHighlightRange,
  };
};

export default useTimelineVariant;
